import React, { useCallback, useEffect, useState } from "react";
import { PageState } from "../../components/PageState";
import { useAuth } from "../../auth/AuthContext";
import { money, n, pct } from "../../utils/format";

type BalanceSheetLine = {
  lineType: string;
  description: string;
  amount: number;
  percentage: number | null;
};

type BalanceSheetReport = {
  referenceDate: string;
  assets: BalanceSheetLine[];
  liabilities: BalanceSheetLine[];
  equity: BalanceSheetLine[];
  indicators: {
    currentRatio: number | null;
    quickRatio: number | null;
    debtRatio: number | null;
    debtEquityRatio: number | null;
    workingCapital: number;
  };
};

const formatRatio = (value: number | null) => {
  if (value === null || value === undefined) return "-";
  return n(value).toFixed(2);
};

const renderLines = (lines: BalanceSheetLine[]) =>
  (lines ?? []).map((line, index) => {
    const isBold = line.lineType.includes("TOTAL");
    return (
      <tr key={index} className={isBold ? "fw-bold" : ""}>
        <td className={line.lineType.startsWith("TOTAL") ? "ps-2" : "ps-4"}>
          {line.description}
        </td>
        <td className="text-end">{money(line.amount)}</td>
        <td className="text-end">{pct(line.percentage)}</td>
      </tr>
    );
  });

export const BalancoPage: React.FC = () => {
  const { client } = useAuth();
  const [referenceDate, setReferenceDate] = useState(
    new Date().toISOString().split("T")[0]
  );
  const [data, setData] = useState<BalanceSheetReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadReport = useCallback(async () => {
    setLoading(true);
    setError(null);
    const params = new URLSearchParams({ referenceDate });
    const response = await client.get(`/reports/financial/balanco?${params.toString()}`);
    if (!response.ok) {
      setError("Falha ao carregar Balanco Patrimonial.");
      setLoading(false);
      return;
    }
    const payload = (await response.json()) as { data?: BalanceSheetReport } | BalanceSheetReport;
    const report = "data" in payload ? payload.data : payload;
    setData(report ?? null);
    setLoading(false);
  }, [client, referenceDate]);

  useEffect(() => { void loadReport(); }, [loadReport]);

  const indicators = data?.indicators ?? {
    currentRatio: null, quickRatio: null, debtRatio: null, debtEquityRatio: null, workingCapital: 0,
  };

  const ratioColor = (v: number | null, good: (x: number) => boolean) => {
    if (v === null) return "#6c757d";
    return good(v) ? "#198754" : "#dc3545";
  };

  return (
    <div className="container py-4">
      <h1 className="mb-3">Balanco Patrimonial</h1>

      <div className="card shadow-sm mb-3" style={{ borderTop: "3px solid #0d6efd" }}>
        <div className="card-body py-2">
          <div className="row g-2 align-items-end">
            <div className="col-md-3">
              <label className="form-label form-label-sm mb-1">Data de Referencia</label>
              <input
                type="date"
                className="form-control form-control-sm"
                value={referenceDate}
                onChange={(e) => setReferenceDate(e.target.value)}
              />
            </div>
            <div className="col-auto d-flex align-items-end">
              <button className="btn btn-primary btn-sm" onClick={loadReport}>Buscar</button>
            </div>
          </div>
        </div>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      <PageState loading={loading} error={error}>
        {data && (
          <>
            <div className="row g-3 mb-3">
              <div className="col-md-3">
                <div className="card shadow-sm h-100" style={{ borderLeft: `4px solid ${ratioColor(indicators.currentRatio, (v) => v >= 1)}` }}>
                  <div className="card-body">
                    <div className="text-muted small">Liquidez Corrente</div>
                    <div className="fw-bold fs-5 mt-1">{formatRatio(indicators.currentRatio)}</div>
                    <small className="text-muted">Ideal ≥ 1</small>
                  </div>
                </div>
              </div>
              <div className="col-md-3">
                <div className="card shadow-sm h-100" style={{ borderLeft: `4px solid ${ratioColor(indicators.quickRatio, (v) => v >= 1)}` }}>
                  <div className="card-body">
                    <div className="text-muted small">Liquidez Seca</div>
                    <div className="fw-bold fs-5 mt-1">{formatRatio(indicators.quickRatio)}</div>
                    <small className="text-muted">Ideal ≥ 1</small>
                  </div>
                </div>
              </div>
              <div className="col-md-3">
                <div className="card shadow-sm h-100" style={{ borderLeft: `4px solid ${ratioColor(indicators.debtRatio, (v) => v <= 0.5)}` }}>
                  <div className="card-body">
                    <div className="text-muted small">Endividamento</div>
                    <div className="fw-bold fs-5 mt-1">
                      {pct(indicators.debtRatio ? indicators.debtRatio * 100 : null)}
                    </div>
                    <small className="text-muted">Ideal ≤ 50%</small>
                  </div>
                </div>
              </div>
              <div className="col-md-3">
                <div
                  className="card shadow-sm h-100"
                  style={{ borderLeft: `4px solid ${indicators.workingCapital >= 0 ? "#198754" : "#dc3545"}` }}
                >
                  <div className="card-body">
                    <div className="text-muted small">Capital de Giro</div>
                    <div className={`fw-bold fs-5 mt-1 ${indicators.workingCapital >= 0 ? "text-success" : "text-danger"}`}>
                      {money(indicators.workingCapital)}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="row g-3">
              <div className="col-md-6">
                <div className="card shadow-sm h-100">
                  <div className="card-body">
                    <h5 className="mb-3">ATIVO</h5>
                    <div className="table-responsive">
                      <table className="table table-sm align-middle">
                        <thead>
                          <tr>
                            <th>Descricao</th>
                            <th className="text-end">Valor</th>
                            <th className="text-end">% Total</th>
                          </tr>
                        </thead>
                        <tbody>{renderLines(data.assets ?? [])}</tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-md-6">
                <div className="card shadow-sm mb-3">
                  <div className="card-body">
                    <h5 className="mb-3">PASSIVO</h5>
                    <div className="table-responsive">
                      <table className="table table-sm align-middle">
                        <thead>
                          <tr>
                            <th>Descricao</th>
                            <th className="text-end">Valor</th>
                            <th className="text-end">% Total</th>
                          </tr>
                        </thead>
                        <tbody>{renderLines(data.liabilities ?? [])}</tbody>
                      </table>
                    </div>
                  </div>
                </div>

                <div className="card shadow-sm">
                  <div className="card-body">
                    <h5 className="mb-3">PATRIMONIO LIQUIDO</h5>
                    <div className="table-responsive">
                      <table className="table table-sm align-middle">
                        <thead>
                          <tr>
                            <th>Descricao</th>
                            <th className="text-end">Valor</th>
                            <th className="text-end">% Total</th>
                          </tr>
                        </thead>
                        <tbody>{renderLines(data.equity ?? [])}</tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </PageState>
    </div>
  );
};
